import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../Lesson.module.css";
import { useAppTranslation } from "./TranslationContext";

export const CustomVideoPlayer = ({ src, checkpoints = [], onComplete, videoId, initialDuration }) => {
  const videoRef = useRef(null);

  // service refs
  const shownRef = useRef(new Set());
  const lastTimeRef = useRef(0);
  const lastSavedTimeRef = useRef(0);
  const completedRef = useRef(false);
  const fixedDurationRef = useRef(null);
  const wrapperRef = useRef(null);

  const { t } = useAppTranslation();

  // state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  const [progress, setProgress] = useState(0);
  const [timeDisplay, setTimeDisplay] = useState("0:00 / --:--");
  const [duration, setDuration] = useState(initialDuration > 0 ? initialDuration : 0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCheckpoint, setActiveCheckpoint] = useState(null);
  const [answer, setAnswer] = useState("");

  const storageKey = useMemo(
    () => `video_progress_${videoId ?? src}`,
    [videoId, src]
  );
  const [savedTime, setSavedTime] = useState(0);
  const [showResumeOptions, setShowResumeOptions] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  // ---------- helpers ----------
  const formatTime = (seconds) => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };


  const getStableDuration = (v) => {
    if (!v) return 0;
    let d = v.duration;
    if (!isFinite(d) || d === Infinity || d <= 0) {
      try {
        if (v.seekable && v.seekable.length > 0) {
          d = v.seekable.end(v.seekable.length - 1);
        }
      } catch {}
    }
    return isFinite(d) && d > 0 ? d : 0;
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const handleVolume = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    const v = videoRef.current;
    if (v) {
      v.volume = val;
      v.muted = val === 0;
      setIsMuted(v.muted);
    }
  };

  const handleFullscreen = () => {
    const container = wrapperRef.current; // общий контейнер плеера
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  };

  const showModal = (cp) => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    setIsPlaying(false);
    setIsModalOpen(true);
    setActiveCheckpoint(cp);
    setAnswer("");
  };

  // ---------- initial/reset on src/change ----------
  useEffect(() => {
    shownRef.current = new Set();
    lastTimeRef.current = 0;
    lastSavedTimeRef.current = 0;
    completedRef.current = false;

    if (initialDuration > 0) {
      fixedDurationRef.current = initialDuration;
      setDuration(initialDuration);
      setTimeDisplay(`0:00 / ${formatTime(initialDuration)}`);
    } else {
      fixedDurationRef.current = null;
      setDuration(0);
      setTimeDisplay("0:00 / --:--");
    }

    setIsModalOpen(false);
    setActiveCheckpoint(null);
    setAnswer("");
    setIsLoading(true);

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const t = parseFloat(stored);
        if (!isNaN(t) && t > 0) {
          setSavedTime(t);
          setShowResumeOptions(true);
        } else {
          setSavedTime(0);
          setShowResumeOptions(false);
        }
      } else {
        setSavedTime(0);
        setShowResumeOptions(false);
      }
    } catch {
      setSavedTime(0);
      setShowResumeOptions(false);
    }
  }, [src, storageKey, initialDuration]);

  // ---------- metadata + loader (NO .load() here) ----------
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    v.pause();
    setIsLoading(true);
    fixedDurationRef.current = initialDuration > 0 ? initialDuration : null;
    v.load();

    const onLoadStart = () => setIsLoading(true);

    const onLoadedMetadata = () => {
      const stable = getStableDuration(v);
      if (stable > 0 && stable > (fixedDurationRef.current || 0)) {
        fixedDurationRef.current = stable;
        setDuration(stable);
      }
      const d = fixedDurationRef.current ?? 0;
      setTimeDisplay(`${formatTime(v.currentTime || 0)} / ${d > 0 ? formatTime(d) : '--:--'}`);
    };

    const onDurationChange = () => {
      const stable = getStableDuration(v);
      // Обновляем только если браузер вернул большее значение (для прогрессивной загрузки)
      if (stable > 0 && stable > (fixedDurationRef.current || 0)) {
        fixedDurationRef.current = stable;
        setDuration(stable);
        setTimeDisplay(`${formatTime(v.currentTime || 0)} / ${formatTime(stable)}`);
      }
    };

    const onCanPlay = () => setIsLoading(false);
    const onCanPlayThrough = () => setIsLoading(false);
    const onWaiting = () => setIsLoading(true);
    const onStalled = () => setIsLoading(true);
    const onPlaying = () => setIsLoading(false);
    const onError = () => setIsLoading(false);


    v.addEventListener("loadstart", onLoadStart);
    v.addEventListener("loadedmetadata", onLoadedMetadata);
    v.addEventListener("durationchange", onDurationChange);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("canplaythrough", onCanPlayThrough);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("stalled", onStalled);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("error", onError);

    // если уже готовы метаданные (инстант при кэше)
  if (v.readyState >= 1) onLoadedMetadata();
  if (v.readyState >= 3) setIsLoading(false);

    return () => {
      v.removeEventListener("loadstart", onLoadStart);
      v.removeEventListener("loadedmetadata", onLoadedMetadata);
      v.removeEventListener("durationchange", onDurationChange);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("canplaythrough", onCanPlayThrough);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("stalled", onStalled);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("error", onError);
    };
  }, [src, initialDuration]);

  // ---------- core player events ----------
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const maybeComplete = () => {
      const d = fixedDurationRef.current ?? getStableDuration(v);
      if (!d || completedRef.current) return;
      if (v.currentTime >= d - 0.2) {
        completedRef.current = true;
        setIsPlaying(false);
        try { localStorage.removeItem(storageKey); } catch {}
        if (onComplete) onComplete();
      }
    };

    const onTimeUpdate = () => {
      const d = fixedDurationRef.current ?? getStableDuration(v);
      const cur = v.currentTime || 0;
      setProgress(d ? (cur / d) * 100 : 0);
      setTimeDisplay(`${formatTime(cur)} / ${formatTime(d)}`);

      checkpoints.forEach((cp) => {
        if (!shownRef.current.has(cp.time) && lastTimeRef.current < cp.time && cur >= cp.time) {
          shownRef.current.add(cp.time);
          showModal(cp);
        }
      });

      if (cur - lastSavedTimeRef.current >= 5) {
        try { localStorage.setItem(storageKey, String(cur)); } catch {}
        lastSavedTimeRef.current = cur;
      }

      lastTimeRef.current = cur;
      maybeComplete();
    };

    const onPause = () => {
      try { localStorage.setItem(storageKey, String(v.currentTime || 0)); } catch {}
    };

    const onEnded = () => {
      setIsPlaying(false);
      maybeComplete();
    };

    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnded);

    const saveAndClose = () => {
      try {
        if (videoRef.current) {
          localStorage.setItem(storageKey, String(videoRef.current.currentTime || 0));
        }
      } catch {}
    };
    const onVisibility = () => { if (document.hidden) saveAndClose(); };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", saveAndClose);

    return () => {
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnded);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", saveAndClose);
    };
  }, [checkpoints, onComplete, storageKey]);

  // ---------- body scroll lock ----------
  // useEffect(() => {
  //   const prev = document.body.style.overflow;
  //   document.body.style.overflow = isModalOpen ? "hidden" : "auto";
  //   return () => { document.body.style.overflow = prev || "auto"; };
  // }, [isModalOpen]);

  // ---------- submit answer (rollback prev.time + 0.5s) ----------
  const handleSubmit = (e) => {
    e.preventDefault();
    const v = videoRef.current;
    if (!v || !activeCheckpoint) return;

    const ok = answer.trim().toLowerCase() === (activeCheckpoint.answer || "").toLowerCase();

    setIsModalOpen(false);

    if (ok) {
      setActiveCheckpoint(null);
      v.play();
      setIsPlaying(true);
      return;
    }

    // Find rollback point from shown history — avoids dependency on current checkpoints prop
    // which parent may have already filtered (e.g. removed correctly-answered ones)
    const prevTime = Array.from(shownRef.current)
      .filter(t => t < activeCheckpoint.time)
      .sort((a, b) => b - a)[0] ?? null;
    const base = prevTime !== null ? prevTime + 1 : 0;

    const stableDuration = fixedDurationRef.current ?? getStableDuration(v);
    const targetTime = stableDuration > 0
      ? Math.min(Math.max(0, base), stableDuration - 0.05)
      : Math.max(0, base);

    // текущий снова покажем, предыдущий не повторяем
    shownRef.current.delete(activeCheckpoint.time);

    v.currentTime = targetTime;
    lastTimeRef.current = targetTime;

    setActiveCheckpoint(null);
    v.play();
    setIsPlaying(true);
  };

  // ---------- render ----------
  return (
    <div className={styles.playerContainer}>
      {/* Общий контейнер, разворачиваем его в полноэкранный режим */}
      <div ref={wrapperRef} className={styles.videoWrapper}>
        <video
          ref={videoRef}
          src={src}
          className={styles.video}
          preload="metadata"
          playsInline
        />
  
        {/* Лоадер поверх видео */}
        {isLoading && !isModalOpen && (
          <div className={styles.loaderOverlay}>
            <div className={styles.loaderText}>
              {t?.("videoLoading") || "Видео загружается, подождите"}
            </div>
          </div>
        )}
  
        {/* Экран возобновления просмотра */}
        {!isPlaying && !isModalOpen && !isLoading && showResumeOptions && (
          <div className={styles.resumeOverlay}>
            <p>{t("alreadyWatched")}</p>
            <div className={styles.resumeBtns}>
              <button
                onClick={() => {
                  const v = videoRef.current;
                  if (!v) return;
                  v.currentTime = 0;
                  lastTimeRef.current = 0;
                  v.play();
                  setIsPlaying(true);
                  setShowResumeOptions(false);
                }}
              >
                {t("startOver")}
              </button>
              <button
                onClick={() => {
                  const v = videoRef.current;
                  if (!v) return;
                  const target = Math.max(0, (savedTime || 0) - 1);
                  setShowResumeOptions(false);

                  const seekAndPlay = () => {
                    lastTimeRef.current = target;
                    lastSavedTimeRef.current = target;

                    const onSeeked = () => {
                      v.removeEventListener('seeked', onSeeked);
                      v.play().catch(console.error);
                      setIsPlaying(true);
                    };
                    v.addEventListener('seeked', onSeeked);
                    v.currentTime = target;
                  };

                  if (v.readyState >= 1) {
                    seekAndPlay();
                  } else {
                    v.addEventListener('loadedmetadata', seekAndPlay, { once: true });
                  }
                }}
              >
                {t("continueWatching")} ({formatTime(Math.max(0, savedTime))})
              </button>

            </div>
          </div>
        )}
  
        {/* Кнопка старт/плей по центру */}
        {!isPlaying && !isModalOpen && !isLoading && !showResumeOptions && (
          <button
            className={styles.startButton}
            onClick={() => {
              videoRef.current?.play();
              setIsPlaying(true);
            }}
          >
            ▶
          </button>
        )}
  
        {/* Модальное окно чекпоинта (внутри videoWrapper, чтобы было видно в fullscreen) */}
        {isModalOpen && activeCheckpoint && (
          <div className={styles.modal}>
            <div className={styles.modalContent}>
              <h3>{t("question")}</h3>
              <p>{activeCheckpoint.question}</p>
              <form onSubmit={handleSubmit}>
                <input
                  type="text"
                  placeholder={t("enterAnswer")}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  required
                />
                <button className="register__btn" type="submit">
                  {t("sendAll")}
                </button>
              </form>
            </div>
          </div>
        )}
      
  
      {/* Панель управления (можно оставить снаружи fullscreen-контейнера) */}
      <div className={styles.controls}>
        <div className={styles.progressContainer}>
          <div className={styles.progressBar} style={{ width: `${progress}%` }} />
        </div>
        <div className={styles.buttonRow}>
          <div className={styles.leftControls}>
            <button onClick={togglePlay}>{isPlaying ? "⏸" : "▶"}</button>
            <button onClick={toggleMute}>{isMuted ? "🔇" : "🔊"}</button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolume}
              className={styles.volumeSlider}
            />
            <span className={styles.timeDisplay}>{timeDisplay}</span>
          </div>
          <div className={styles.rightControls}>
            <button onClick={handleFullscreen}>⛶</button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
  
};
